import { useQueries, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@geefamily/api';
import type { CoinCode } from './CoinMark';

/**
 * The instruments shown in the markets card.
 *
 * Every one is a symbol Binance actually quotes, because every price on this
 * card is a real one. The reference design also listed HYPE and TSLA; neither is
 * on this feed, and a made-up price on a page about betting on price movements
 * is the one number that must never be invented — somebody could act on it.
 *
 * Gold is PAXG, a token redeemable for an ounce, so it tracks spot closely. It
 * is labelled as PAXG rather than XAU for that reason: near is not the same.
 */
// `as const` rather than an annotation, so `instruments[0]` is known to exist —
// the demo panel starts on it. `satisfies` still checks every code against the
// marks that are actually drawn, so adding a row with no mark fails to compile.
export const instruments = [
  { symbol: 'BTCUSDT', code: 'BTC', name: 'Bitcoin' },
  { symbol: 'ETHUSDT', code: 'ETH', name: 'Ethereum' },
  { symbol: 'SOLUSDT', code: 'SOL', name: 'Solana' },
  { symbol: 'XRPUSDT', code: 'XRP', name: 'XRP' },
  { symbol: 'DOGEUSDT', code: 'DOGE', name: 'Dogecoin' },
  { symbol: 'BNBUSDT', code: 'BNB', name: 'BNB' },
  { symbol: 'AVAXUSDT', code: 'AVAX', name: 'Avalanche' },
  { symbol: 'PAXGUSDT', code: 'PAXG', name: 'Altın (PAXG)' },
] as const satisfies readonly { symbol: string; code: CoinCode; name: string }[];

export interface Quote {
  symbol: string;
  price: number;
  changePercent: number;
}

const marketKeys = queryKeys.module('moon').entity('markets');

// Binance's dedicated public market-data host; no account or trading endpoints.
// https://developers.binance.com/en/docs/products/spot/rest-api
const binance = 'https://data-api.binance.vision/api/v3';

/**
 * Live prices, straight from Binance's public API.
 *
 * From the browser rather than through our own API: the endpoint is public and
 * sends `access-control-allow-origin: *`, so proxying it would add a hop, a
 * cache to invalidate and an outage of our own for no gain.
 *
 * Every fifteen seconds. Faster reads as live and costs nothing here, but it is
 * a public endpoint with rate limits shared across everyone behind a NAT — and
 * a ticker that updates faster than a person can read it is decoration.
 */
export function useQuotes() {
  const symbols = instruments.map((i) => i.symbol);

  return useQuery({
    queryKey: marketKeys.list({ symbols }),
    queryFn: async ({ signal }): Promise<Quote[]> => {
      const url = `${binance}/ticker/24hr?symbols=${encodeURIComponent(JSON.stringify(symbols))}`;
      const response = await fetch(url, { signal });

      if (!response.ok) {
        throw new Error(`Binance ${response.status}`);
      }

      const rows = (await response.json()) as {
        symbol: string;
        lastPrice: string;
        priceChangePercent: string;
      }[];

      return rows.map((row) => ({
        symbol: row.symbol,
        price: Number(row.lastPrice),
        changePercent: Number(row.priceChangePercent),
      }));
    },
    refetchInterval: 15_000,
    // Pause background polling; the query refreshes when the tab becomes active.
    refetchIntervalInBackground: false,
    // One retry: a ticker that hammers a failing endpoint every fifteen seconds
    // is how an IP gets rate-limited.
    retry: 1,
    staleTime: 10_000,
  });
}

/**
 * 24 hourly closes per instrument, for the sparklines.
 *
 * Separate from the prices and much slower: an hourly candle cannot change more
 * than once an hour, so refetching it every fifteen seconds would be eight
 * pointless requests a minute.
 */
export function useSparklines() {
  return useQueries({
    queries: instruments.map((instrument) => ({
      queryKey: marketKeys.detail(`spark:${instrument.symbol}`),
      queryFn: async ({ signal }: { signal: AbortSignal }): Promise<number[]> => {
        const response = await fetch(
          `${binance}/klines?symbol=${instrument.symbol}&interval=1h&limit=24`,
          { signal },
        );

        if (!response.ok) {
          throw new Error(`Binance ${response.status}`);
        }

        // [openTime, open, high, low, close, ...] — index 4 is the close.
        const candles = (await response.json()) as unknown[][];

        return candles.map((candle) => Number(candle[4]));
      },
      staleTime: 5 * 60_000,
      retry: 1,
    })),
    combine: (results) => {
      const bySymbol = new Map<string, number[]>();

      results.forEach((result, index) => {
        const instrument = instruments[index];

        if (instrument && result.data) {
          bySymbol.set(instrument.symbol, result.data);
        }
      });

      return bySymbol;
    },
  });
}

/**
 * Formats a price with a sensible number of decimals for its size.
 *
 * DOGE at two decimals is `$0.08` and loses the movement the row exists to
 * show; Bitcoin at six is a wall of digits. The threshold is the magnitude, not
 * the asset, so a new instrument needs no special case.
 */
export function formatPrice(value: number): string {
  const decimals = value >= 100 ? 2 : value >= 1 ? 4 : 6;

  return `$${value.toLocaleString('tr-TR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

export function formatPercent(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}
